import {authService, userService} from "../client/connection/Services";
import {
    closeWindow,
    getOpenerOrigin, getQueryParam,
    isFacebookBrowser, isIframe, openWindowWithOpener,
} from "./Utils";
import {iFrameConnection} from "./services/IFrameConnection";
import {IFrameMessages} from "../blink-sdk/messaging/IFrameMessages";
import {Router} from "../stem-core/src/ui/Router";
import {
    BLINK_PAY_URL,
    LOGIN_WITH_EMAIL_CODE_URL,
    SIGNUP_EXTERNAL_URL
} from "./Constants";
import {LoadingSpinner} from "./ui/LoadingSpinner";
import {NOOP_FUNCTION, getCookie, setCookie} from "../stem-core/src/base/Utils";
import {AuthToken} from "../client/connection/AuthToken";
import {Device} from "../stem-core/src/base/Device";
import {AnalyticsEventType, dispatchAnalyticsEvent} from "../blink-sdk/utils/AnalyticsClient";
import {iFrameState} from "./services/IFrameState";
import {authFormService} from "./services/AuthFormService";


export function closeTabAndSendToken(sendUserData=false) {
    if (window.opener && getOpenerOrigin() === window.origin) {
        iFrameConnection.sendToOpener(IFrameMessages.USER_TOKEN_UPDATE, {
            merchantToken: authService.merchantToken.isAuthenticated() ? authService.merchantToken.getTokenObjectStringified() : null,
            token: authService.token.getTokenObjectStringified(),
            userData: sendUserData ? userService.userData : undefined,
        });
    }

    closeWindow();
}

export function useRedirectsInsteadOfTabs() {
    return isFacebookBrowser();
}

export function canWriteCookies() {
    try {
        const cookieValue = +Date.now();
        // Set a cookie that expires in a second.
        setCookie("blinkCookieTest", cookieValue, 1);
        return parseInt(getCookie("blinkCookieTest")) === cookieValue;
    } catch (e) {
        return false;
    }
}

// TODO: Check and improve path for third party storage blocked.
export class AuthHelper {
    static authenticationCallbacks = [];
    static oauth1NewTab = null;
    static oauth2NewTab = null;
    static closeTabAfterAuth = getQueryParam("closeTabAfterAuth");

    static _getMerchantRedirectPage() {
        try {
            return sessionStorage.getItem("merchantPageUrl");
        } catch (e) {
            return null;
        }
    }

    static _shouldRedirectToMerchantPage() {
        return !!this._getMerchantRedirectPage();
    }

    static _redirectToUrl(redirectUrl = null) {
        if (!redirectUrl) {
            if (!isIframe()) {
                Router.changeURL("/");
            }
            return;
        }

        if (isIframe()) {
            iFrameConnection.sendToSDK(IFrameMessages.REDIRECT_TO_URL, {url: redirectUrl});
        } else {
            openWindowWithOpener(redirectUrl, "_self");
        }
    }

    static redirectToMerchantPage() {
        this._redirectToUrl(this._getMerchantRedirectPage());
    }

    // <-- Pay wall / Donation wall -->

    static saveMerchantPageUrlForRedirect(url) {
        try {
            sessionStorage.setItem("merchantPageUrl", url);
        } catch (e) {
        }
    }

    // TODO: deprecate
    static addAuthenticationCallback(callback = NOOP_FUNCTION) {
        // After authentication, we need to redirect to the initial wall.
        this.authenticationCallbacks.push(
            authService.addListener(AuthToken.EventTypes.LOGIN, callback));
    }

    static clearAuthenticationCallbacks() {
        for (const callback of this.authenticationCallbacks) {
            callback.cleanup();
        }
        this.authenticationCallbacks = [];
    }

    // <-- OAuth -->

    static _redirectToOauthLogin(url) {
        // We can't redirect inside an iframe, so we need to redirect the parent page.
        if (!useRedirectsInsteadOfTabs() && this.oauth2NewTab) {
            this.oauth2NewTab.location.href = url;
        } else if (isIframe()) {
            if (useRedirectsInsteadOfTabs()) {
                this.saveMerchantPageUrlForRedirect(iFrameState.merchantPageUrl);
            }
            iFrameConnection.sendToSDK(IFrameMessages.REDIRECT_TO_URL,
                {url, newTab: !useRedirectsInsteadOfTabs()});
        } else {
            // On facebook browser we can't have multiple tabs.
            openWindowWithOpener(url, useRedirectsInsteadOfTabs() ? "_self" : "_blank");
        }
    }

    // User clicked on a 3rd party oauth2 client login button; Open the oauth login url for that client.
    static oauth2Login(url) {
        // We can open a new tab only when we are top level and not in the facebook browser.
        if (!useRedirectsInsteadOfTabs()) {
            this.oauth2NewTab = openWindowWithOpener();
        }
        this._redirectToOauthLogin(url);
    }

    // Same as above, but for a oauth1 client.
    static oauth1Login() {
        // In oauth1 we need to do a call to our backend first, so the entire flow will be async. However, some browsers
        // block tabs open through async flows, so we open the tab synchronously before the call is finished. We won't
        // be able to open the new tab on Facebook browser (because it allows for only one active tab at a time) and in
        // an iframe; for these cases we will redirect once we get the url from our backend.
        if (!useRedirectsInsteadOfTabs()) {
            this.oauth1NewTab = openWindowWithOpener();
        }
    }

    // We received the client url from our backend, now we can redirect / set the url on the previously open tab
    static oauth1TokenReceivedCallback(url) {
        // Set the url on the previously opened tab.
        if (!useRedirectsInsteadOfTabs() && this.oauth1NewTab) {
            this.oauth1NewTab.location.href = url;
        } else {
            this._redirectToOauthLogin(url);
        }
    }

    // We have the oauth token, now let's login the user and redirect.
    static _oauthExecuteLogin(oauthClient, socialAccountsManager, oauthObject, redirect) {
        oauthClient.setLoginCallback(redirect);
        oauthClient.setLoginErrorCallback(redirect);

        socialAccountsManager.handleRedirectAuthInfo(oauthObject);
    }

    // The 3rd party oauth client redirects to one of our pages with the oauth token. Now, we can attempt to login the
    // user.
    static oauthHandleLoginRedirect(oauthClient, socialAccountsManager, oauthObject) {
        let redirect;
        if (this._shouldRedirectToMerchantPage()) {
            // We arrived here from an iframe on a merchant's domain through redirects, so we should redirect back to
            // the initial page.
            redirect = () => this.redirectToMerchantPage()
        } else if (getOpenerOrigin() === window.origin) {
            // This tab was opened externally, so we should close it after the login is finished.
            redirect = () => setTimeout(closeTabAndSendToken, Device.isMobileDevice() ? 100 : 0);
        } else {
            // We logged in via redirects. Now redirect to the home page.
            redirect = () => Router.changeURL("/");
        }

        this._oauthExecuteLogin(oauthClient, socialAccountsManager, oauthObject, redirect);
    }

    // User has interrupted the oauth login or an error has occured.
    static oauthHandleError() {
        if (!useRedirectsInsteadOfTabs()) {
            closeWindow();
        } else if (isIframe()) {
            Router.changeURL(SIGNUP_EXTERNAL_URL);
        } else if (this._shouldRedirectToMerchantPage()) {
            this.redirectToMerchantPage();
        } else {
            Router.changeURL("/signup");
        }
    }

    // Make the oauth backend request and handle the response cases.
    static async oauthHandleLoginResponse(endpoint, postData) {
        LoadingSpinner.show();
        try {
            await authService.loginAtEndpoint(endpoint, postData);
        } finally {
            LoadingSpinner.hide();
        }
    }

    // <-- Login / Sign up -->
    static async _ensureTokenSetOnMainWebsiteAfterBackendCall(backendCall, openCookieTab = true) {
        LoadingSpinner.show();
        // This is used when the user gets signed in the iframe but the token isn't set on the main website. The current
        // cases when this needs to be done is on safari (because you can't change the main domain's storage inside an
        // iframe) and when the third party storage is blocked (because you don't even have access to any kind of
        // storage from the iframe).
        let setCookieTab;
        if (!canWriteCookies() && openCookieTab && !useRedirectsInsteadOfTabs()) {
            setCookieTab = openWindowWithOpener();
        }

        return new Promise( async (resolve, reject) => {
            let response;
            try {
                response = await backendCall();
                LoadingSpinner.hide();
            } catch (error) {
                LoadingSpinner.hide();
                if (setCookieTab) {
                    setCookieTab.close();
                }
                reject(error);
                return;
            }

            const setCookieUrl = `${BLINK_PAY_URL}/set-cookie?token=` + encodeURI(authService.token.getTokenObjectStringified());
            if (setCookieTab) {
                // Once the url is set, the page loads, the cookie is set on our domain and then the page closes itself.
                setCookieTab.location.href = setCookieUrl;
            }
            if (!canWriteCookies() && openCookieTab && useRedirectsInsteadOfTabs()) {
                // TODO: Are we sure we are in an iframe here? Why? At least write a comment about it
                this._redirectToUrl(setCookieUrl + "&merchantRedirectUrl="
                    + encodeURI(iFrameState.merchantPageUrl));
            }
            authFormService.setIsRegistration(!!response.registration);

            resolve(response);
        });
    }

    static async requestLoginCode(payload) {
        dispatchAnalyticsEvent(AnalyticsEventType.REQUEST_EMAIL_ACCESS_CODE);
        return this._ensureTokenSetOnMainWebsiteAfterBackendCall(() => authService.requestLoginCode(payload), false);
    }

    static async confirmEmailByCode(code) {
        return this._ensureTokenSetOnMainWebsiteAfterBackendCall(() => authService.confirmEmailByCode({code}));
    }

    static async loginWithEmailCode(code) {
        return this._ensureTokenSetOnMainWebsiteAfterBackendCall(() => authService.loginWithEmailCode({code}))
    }

    static signUpAction() {
        const {registrationMail, confirmationCodeType} = authFormService;
        Router.changeURL(`/${LOGIN_WITH_EMAIL_CODE_URL}?registrationMail="${registrationMail}"&confirmationCodeType="${confirmationCodeType}"`)
    }

    // This gets called after user authenticates by clicking the login link (received in the login code email)
    static completedLoginByEmailKeyAction(redirectUrl = "") {
        if (redirectUrl && redirectUrl.trim().length > 0) {
            openWindowWithOpener(redirectUrl, "_self");
        } else {
            Router.changeURL("/");
        }
    }

    // <-- AuthSuccessSpinner -->

    // This gets called when we display a confirmation message (we display it after either accepting ToS for oauth
    // account creation, or after confirming the email via normal account creation); this also gets called in the email
    // code confirmation page when the user completed the account creation in another tab.
    static confirmedAuthenticationAction() {
        if (this._shouldRedirectToMerchantPage()) {
            // We got here starting from an iframe and through many redirects, so let's redirect to the initial page.
            this.redirectToMerchantPage();
        } else if (this.closeTabAfterAuth) {
            closeTabAndSendToken();
        } else if (isIframe()) {
            // We are in an iframe; the correct redirect is being made in previous code after we are authenticated, so
            // now nothing is left to do.
        } else {
            // The authentication was not made externally, so just redirect to the homepage.
            Router.changeURL("/");
        }
    }
}
